import {
  CaseSizeRuleCreateRequestType,
  PriceTierListResponseType,
} from "@sellar-io/api-v2/types";
import {
  Button,
  H5,
  Icon,
  useFullPageDialogContext,
  InputNumber,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "@sellar-io/components";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { Alert } from "../../../../../components/alert/Alert";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
  TableLegacyTD,
} from "../../../../../components/table/TableLegacy";
import { apiV2AssertError } from "../../../../../hooks/api/v2/apiV2Request";
import { usePriceTierList } from "../../../../../hooks/api/v2/priceTier/usePriceTierList";
import { usePriceTierOrderRuleUpdate } from "../../../../../hooks/api/v2/priceTierOrderRule/usePriceTierOrderRuleUpdate";
import { generatePriceTierOrderRuleDescription } from "../../../../../utils/sellarHelpers";
import { generateProductItemName } from "../../../helpers/generateProductItemName";
import { CaseSizeRuleCreateDialogPage } from "../CaseSizeRuleCreateDialogPage";
import { getPriceTiersWithOrderRuleContainerType } from "../helpers/priceTierOrderRules";
import { useRemoteFeatureFlags } from "../../../../../hooks/useRemoteFeatureFlags";

type PriceTier = PriceTierListResponseType[number];
type PriceTierOrderRule = Pick<
  PriceTier,
  "PriceTierOrderRules"
>["PriceTierOrderRules"][number];
type UpdateMoqRuleFormInputs = Pick<
  PriceTierOrderRule,
  "itemType" | "quantity"
>;

const sortByIdAsc = <T extends { id: number }>(a: T, b: T) => a.id - b.id;

const CaseSizeRuleLegend = ({
  caseSizeRule,
  className,
}: {
  caseSizeRule: CaseSizeRuleCreateRequestType;
  className?: string;
}) => {
  return (
    <div className={twMerge("flex rounded-lg bg-gray-100 p-4", className)}>
      <H5 className="inline">1 case =</H5>&nbsp;
      {caseSizeRule.quantity}&nbsp;x&nbsp;
      {generateProductItemName(caseSizeRule)}
    </div>
  );
};

const TableRow = ({
  name,
  ruleList,
  onExpandPriceTierOrderRuleClicked,
}: {
  name: string;
  onExpandPriceTierOrderRuleClicked: (rule: PriceTierOrderRule) => void;
  ruleList: PriceTierOrderRule[];
}) => {
  return (
    <TableLegacyTR>
      <TableLegacyTD>{name}</TableLegacyTD>
      <TableLegacyTD>
        {ruleList.sort(sortByIdAsc).map((rule, index) => {
          const ruleAsDescription = generatePriceTierOrderRuleDescription(rule);

          return (
            <span key={index} className="flex justify-between">
              <span className="flex items-center">{ruleAsDescription}</span>
              <span>
                <Button
                  color={"transparent"}
                  onClick={() => onExpandPriceTierOrderRuleClicked(rule)}
                >
                  <Icon icon={"CHEVRON_RIGHT"} />
                </Button>
              </span>
            </span>
          );
        })}
      </TableLegacyTD>
    </TableLegacyTR>
  );
};

const usePriceTierListWithContainerRule = ({
  containerType,
}: {
  containerType: "bottle" | "can";
}) => {
  const priceTiersListQuery = usePriceTierList({
    sortBy: "name",
    sortDir: "asc",
  });
  const priceTierList = priceTiersListQuery.data?.pages[0] ?? [];
  const priceTierOrderRuleList = getPriceTiersWithOrderRuleContainerType({
    priceTierList,
    containerType,
  });
  {
    return {
      priceTierOrderRuleList: priceTierOrderRuleList,
    };
  }
};

const PriceTierOrderRulesList = ({
  caseSizeRule,
  onConfirm,
  onRuleSelected,
  onBack,
  onDismiss,
}: {
  caseSizeRule: CaseSizeRuleCreateRequestType;
  onRuleSelected: (orderRule: PriceTierOrderRule) => void;
  onConfirm: () => void;
  onBack: () => void;
  onDismiss: () => void;
}) => {
  // find price tiers with order rules that conform to the container type specified in the case size rule
  const { priceTierOrderRuleList } = usePriceTierListWithContainerRule({
    containerType: caseSizeRule.containerType,
  });

  const unbundlingRelease2BEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2B",
  );

  return (
    <>
      <DialogNavigation
        title={"Minimum order quantities"}
        description={`The following minimum order quantities set up refer to a ‘case’ of this product item. Are these still correct?`}
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <DialogContent>
        {priceTierOrderRuleList.length ? (
          <>
            <CaseSizeRuleLegend className="mb-5" caseSizeRule={caseSizeRule} />
            <TableLegacyContainer>
              <TableLegacy>
                <TableLegacyTHead>
                  <TableLegacyTR>
                    <TableLegacyTH>
                      {unbundlingRelease2BEnabled ? "Price list" : "Price tier"}
                    </TableLegacyTH>
                    <TableLegacyTH>Minimum order rule</TableLegacyTH>
                  </TableLegacyTR>
                </TableLegacyTHead>

                <TableLegacyTBody>
                  {priceTierOrderRuleList.map((priceTierWithOrderRule) => {
                    return (
                      <TableRow
                        key={priceTierWithOrderRule.id}
                        name={priceTierWithOrderRule.name}
                        ruleList={priceTierWithOrderRule.PriceTierDiscounts}
                        onExpandPriceTierOrderRuleClicked={(rule) => {
                          onRuleSelected(rule);
                        }}
                      />
                    );
                  })}
                </TableLegacyTBody>
              </TableLegacy>
            </TableLegacyContainer>
          </>
        ) : (
          <H5>
            You do not have any minimum order quantities set for this product
            item type.
          </H5>
        )}
      </DialogContent>
      <DialogCTA>
        <Button color={"transparent"} onClick={onBack}>
          Back
        </Button>

        <Button onClick={onConfirm}>Next</Button>
      </DialogCTA>
    </>
  );
};

const UpdateMoqRule = ({
  caseSizeRule,
  priceTierOrderRule,
  onBack,
}: {
  caseSizeRule: CaseSizeRuleCreateRequestType;
  priceTierOrderRule: PriceTierOrderRule;
  onBack: () => void;
}) => {
  const { register, handleSubmit, reset } = useForm<UpdateMoqRuleFormInputs>({
    shouldUseNativeValidation: true,
  });

  const priceTierOrderRuleUpdateMutation = usePriceTierOrderRuleUpdate(
    priceTierOrderRule.id,
  );

  useEffect(() => {
    reset(priceTierOrderRule);
  }, [reset, priceTierOrderRule]);

  return (
    <>
      <form
        onSubmit={handleSubmit((data) => {
          priceTierOrderRuleUpdateMutation.mutate(data, {
            onSuccess: () => {
              onBack();
            },
          });
        })}
      >
        <DialogNavigation
          title={"Edit minimum order quantity"}
          leftIcon={"CHEVRON_LEFT"}
          onClickLeftIcon={onBack}
        />

        <DialogContent className={"flex flex-col gap-4"}>
          {priceTierOrderRuleUpdateMutation.isError ? (
            <Alert
              type={"error"}
              className={"mb-5 w-full"}
              message={
                apiV2AssertError(priceTierOrderRuleUpdateMutation.error)
                  ?.response?.data.message ?? "Unknown error"
              }
            />
          ) : null}

          <CaseSizeRuleLegend className="mb-5" caseSizeRule={caseSizeRule} />

          <div>
            <InputLabel>Container type</InputLabel>
            <InputContainer>
              <Icon icon={"LOCK"} size={1.1} className={"text-neutral-300"} />
              <InputSelect
                {...register("itemType", { required: true })}
                disabled
              >
                <InputOption key={"any"} value={"any"}>
                  Any
                </InputOption>
                <InputOption value={"bottle"}>Cases of bottles</InputOption>
                <InputOption value={"can"}>Cases of cans</InputOption>
              </InputSelect>
            </InputContainer>
          </div>
          <div>
            <InputLabel>Container quantity</InputLabel>
            <InputContainer>
              <InputNumber
                step={"1"}
                min={1}
                {...register("quantity", {
                  required: true,
                  valueAsNumber: true,
                })}
              />
            </InputContainer>
          </div>
        </DialogContent>
        <DialogCTA>
          <Button
            type="submit"
            loading={priceTierOrderRuleUpdateMutation.isPending}
          >
            Update
          </Button>
        </DialogCTA>
      </form>
    </>
  );
};

export const UpdateMoqRules = ({
  caseSizeRule,
}: {
  caseSizeRule: CaseSizeRuleCreateRequestType;
}) => {
  const { onDismiss, setPage } = useFullPageDialogContext();
  const [selectedOrderRule, setSelectedOrderRule] = useState<
    PriceTierOrderRule | undefined
  >();

  return (
    <>
      {selectedOrderRule ? (
        <UpdateMoqRule
          caseSizeRule={caseSizeRule}
          priceTierOrderRule={selectedOrderRule}
          onBack={() => setSelectedOrderRule(undefined)}
        />
      ) : (
        <PriceTierOrderRulesList
          caseSizeRule={caseSizeRule}
          onConfirm={() =>
            setPage(CaseSizeRuleCreateDialogPage.VOLUME_DISCOUNT_RULES)
          }
          onRuleSelected={(orderRule: PriceTierOrderRule) => {
            setSelectedOrderRule(orderRule);
          }}
          onBack={() => {
            setPage(CaseSizeRuleCreateDialogPage.CREATE_PACKAGE_RULE);
          }}
          onDismiss={() => onDismiss?.()}
        />
      )}
    </>
  );
};
